<!--
/*******************************************************************************
*@ 컴포넌트명   : CalendarRange.vue
*@ 컴포넌트설명 : 기간달력 공통컴포넌트
*@ 사용라이브러리: https://vcalendar.io
********************************************************************************
** 날짜/시      작성자   변경내역
********************************************************************************
** 
********************************************************************************/
-->
<template>
  <div class="form-row">
    <div class="item-tit">
      {{ title || $t(titleI18n) }}
      <required v-if="isValidator && useRequired" />
    </div>
    <div class="item-desc">
      <!-- input-icon(from) ~ input-icon(to) 형태  -->
      <div class="form-datepicker-group">
        <vuelidate-form :validator="isValidator ? $v : null" :validator-params="errMsgParam" :messages="errMsg">
          <div class="form-datepicker">
            <div
              class="input-text-field right-icon"
              :class="{ disabled: fromDateDisabled }"
              @click="
                () => {
                  (isFromOpen = !fromDateDisabled), (isCalendarOpen = !fromDateDisabled);
                }
              "
            >
              <!-- isFromOpen = true, isCalendarOpen = true -->
              <input type="text" class="form-control" :value="$getGlobalDateFmt(fromDate)" readonly />
              <i class="icon icon-calendar"></i>
            </div>
            <div class="form-text-dash"></div>
            <div
              class="input-text-field right-icon"
              :class="{ disabled: toDateDisabled }"
              @click="
                () => {
                  (isFromOpen = false), (isCalendarOpen = !toDateDisabled);
                }
              "
            >
              <input type="text" class="form-control" :value="$getGlobalDateFmt(toDate)" readonly />
              <i class="icon icon-calendar"></i>
            </div>
          </div>
        </vuelidate-form>
        <!-- 날짜 선택 버튼 영역 -->
        <div class="form-datepicker-btn" v-if="isShowRangeBtn">
          <template v-for="(data, i) in filterRangeTextList">
            <button ref="rangeBtn" :class="['btn sm inline', data.value == defaultRangeIndex ? 'selected' : '']" :key="i" @click="changeRangeDate(data.value)">
              {{ $t(data.label) }}
            </button>
          </template>
        </div>
      </div>
      <!-- 달력보기 팝업 -->
      <popup :opened.sync="isCalendarOpen" :use-footer="true" :no-negative-button="true" :title="title" :btn-area-padding="true">
        <template #content>
          <article class="contents calendar">
            <!-- from -->
            <v-date-picker
              v-if="isFromOpen"
              ref="calendarFrom"
              v-model="rangeDate.start"
              locale="en"
              :masks="{ title: 'MM/YYYY' }"
              :min-date="$getStringToDate(minDate)"
              :max-date="$getStringToDate(maxDate)"
              :attributes="[
                { key: 'today', highlight: true, dot: true },
                { dot: { style: { background: '#0091ff' } }, dates: [rangeDate.end] },
              ]"
              @dayclick="dayClick"
            />
            <!-- to -->
            <v-date-picker
              v-else
              ref="calendarTo"
              v-model="rangeDate.end"
              locale="en"
              :masks="{ title: 'MM/YYYY' }"
              :min-date="$getStringToDate(minDate)"
              :max-date="$getStringToDate(maxDate)"
              :attributes="[{ dot: { style: { background: '#0091ff' } }, dates: [rangeDate.start] }]"
              @dayclick="dayClick"
            />
          </article>
        </template>
        <template #footer>
          <div class="btn-area--full">
            <button class="btn md gray" @click="cancel">{{ /*취소*/ $t("MCOM-0000002") }}</button>
            <button class="btn md normal" @click="confirm">{{ /*확인*/ $t("MCOM-0000003") }}</button>
          </div>
        </template>
      </popup>
      <modal-pop :opened.sync="validationPopup" class="modal">
        <template #content>{{ $t(validationErrorMsg) }}</template>
      </modal-pop>
    </div>
  </div>
</template>

<script>
import { commonProps, calendarRangeDateSet, validation } from "@/mixins/basicComponent";
import BaseContent from "@/components/BaseContent";
import { mapFields } from "vuex-map-fields";

export default {
  name: "CalendarRange",
  extends: BaseContent,
  mixins: [commonProps, calendarRangeDateSet, validation],
  props: {
    errMsg: Object,
    fromDateDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rangeTextList: [
        { label: "MCOM-0000036", value: this.$config.CALENDAR_RANGE_ETC }, //별도 요청 시까지
        { label: "MCOM-0000037", value: this.$config.CALENDAR_RANGE_TODAY }, //Today
        { label: "MCOM-0000038", value: this.$config.CALENDAR_RANGE_1W }, //-1W
        { label: "MCOM-0000039", value: this.$config.CALENDAR_RANGE_1M }, //-1M
        { label: "MCOM-0000040", value: this.$config.CALENDAR_RANGE_3M }, //-3M
        { label: "MCOM-0000041", value: this.$config.CALENDAR_RANGE_6M }, //-6M
        { label: "MCOM-0000042", value: this.$config.CALENDAR_RANGE_12M }, //-12M
        { label: "MCOM-0000043", value: this.$config.CALENDAR_RANGE_1YEAR }, //1 Year ago
        { label: "MCOM-0000044", value: this.$config.CALENDAR_RANGE_2YEAR }, //2 Year ago
        { label: "MCOM-0000045", value: this.$config.CALENDAR_RANGE_3YEAR }, //3 Year ago
        { label: "MCOM-0000046", value: this.$config.CALENDAR_RANGE_4YEAR }, //4 Year ago
        { label: "MCOM-0000047", value: this.$config.CALENDAR_RANGE_3M_AFTER }, //+3M
        { label: "MCOM-0000048", value: this.$config.CALENDAR_RANGE_6M_AFTER }, //+6M
        { label: "MCOM-0000049", value: this.$config.CALENDAR_RANGE_12M_AFTER }, //+12M
      ],
      filterRangeTextList: [],
      toDateDisabled: false,

      rangeDate: {
        start: this.$getStringToDate(),
        end: this.$getStringToDate(),
      },
      invalid: false,
      validationPopup: false,
      validationErrorMsg: "",
      isFromOpen: false,
    };
  },
  created() {
    let rangeList =
      this.rangeOption.length == 0
        ? [
            this.$config.CALENDAR_RANGE_TODAY,
            this.$config.CALENDAR_RANGE_1W,
            this.$config.CALENDAR_RANGE_1M,
            this.$config.CALENDAR_RANGE_3M,
            this.$config.CALENDAR_RANGE_6M,
            this.$config.CALENDAR_RANGE_12M,
          ]
        : this.rangeOption;
    this.filterRangeTextList = this.rangeTextList.filter(item => {
      return rangeList.includes(item.value);
    });
  },
  mounted() {
    if (this.$route.name == "mmrSalaryApplyRsultInq") {
      this.maxDate = this.moment(this.sessionInfo.userInfo.nxbizYmd).format("YYYYMMDD");
      this.minDate = this.moment(this.sessionInfo.userInfo.nxbizYmd).add(-10, "y").format("YYYYMMDD");
    }
    if (_.isEmpty(this.fromDate) || _.isEmpty(this.toDate)) {
      this.changeRangeDate(this.defaultRangeIndex);
    } else {
      this.$set(this.rangeDate, "start", this.$getStringToDate(this.fromDate));
      this.$set(this.rangeDate, "end", this.$getStringToDate(this.toDate));
      this.diffDate();
    }
  },
  validations() {
    let customValidations = {};
    if (this.$attrs) {
      for (const key in this.$attrs) {
        if (key == this.$config.VALIDATOR_REQUIRED) {
          customValidations = {
            ...customValidations,
          };
          this.useRequired = true;
        }
      }
    }

    return customValidations;
  },
  computed: {
    ...mapFields(["sessionInfo"]),
    /**
     * from 날짜 YYYYMMDD 형식으로 masking 처리
     * @returns {String} YYYYMMDD
     */
    startDateString() {
      return this.$getDateToString(this.rangeDate.start);
    },
    /**
     * to 날짜 YYYYMMDD 형식으로 masking 처리
     * @returns {String} YYYYMMDD
     */
    endDateString() {
      return this.$getDateToString(this.rangeDate.end);
    },
    isCalendarOpen: {
      get() {
        return this.calendarOpened;
      },
      set(value) {
        this.calendarOpened = value;
        this.$emit("calendarOpen", value);
      },
    },
  },
  methods: {
    /**
     * 기간 버튼 update
     * @param {Number} i 적용할 버튼 index
     */
    changeRangeDate(i) {
      this.toDateDisabled = i == this.$config.CALENDAR_RANGE_ETC;

      if (this.toDateDisabled) {
        //별도 요청시일 때
        this.setDate({ start: this.startDateString, end: "99991231" });
      } else if (i >= this.$config.CALENDAR_RANGE_3M_AFTER && i <= this.$config.CALENDAR_RANGE_12M_AFTER) {
        if (this.fromDate == "") {
          if (this.$route.name == "mmrSalaryApplyRsultInq") {
            this.$set(this.rangeDate, "start", this.moment(this.sessionInfo.userInfo.nxbizYmd).add(1, "d"));
            this.setDate(this.changeDate(i, this.moment(this.sessionInfo.userInfo.nxbizYmd).add(1, "d").format("YYYYMMDD")));
          } else {
            this.$set(this.rangeDate, "start", this.moment().add(1, "d"));
            this.setDate(this.changeDate(i, this.moment().add(1, "d").format("YYYYMMDD")));
          }
        } else {
          this.setDate(this.changeDate(i, this.fromDate));
        }
      } else {
        if (this.$route.name == "mmrSalaryApplyRsultInq") {
          this.$set(this.rangeDate, "end", this.$getStringToDate(this.sessionInfo.userInfo.nxbizYmd));
          this.setDate(this.changeDate(i, this.$getDateToString(this.sessionInfo.userInfo.nxbizYmd)));
        } else {
          this.$set(this.rangeDate, "end", this.$getStringToDate());
          this.setDate(this.changeDate(i, this.$getDateToString()));
        }
      }
    },
    /**
     * from, to set date
     * @param {Object} date {from : 'YYYYMMDD', to:'YYYYMMDD'}
     */
    setDate(date) {
      this.$set(this.rangeDate, "start", this.$getStringToDate(date.start));
      this.$set(this.rangeDate, "end", this.$getStringToDate(date.end));
      this.$emit("update:fromDate", date.start);
      this.$emit("update:toDate", date.end);
      //별도요청시 선택되어 있으면 pass
      if (!this.toDateDisabled) {
        this.diffDate();
      } else {
        for (const data of this.$refs.rangeBtn) {
          data.classList.remove("selected");
        }
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_ETC });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      }
    },
    /**
     * 선택한 날짜 범위 계산하여 기간버튼 활성화
     */
    diffDate() {
      if (!this.isShowRangeBtn) return true;

      const diff = this.moment(this.rangeDate.start).diff(this.moment(this.rangeDate.end), "days");

      for (const data of this.$refs.rangeBtn) {
        data.classList.remove("selected");
      }
      if (diff == 0) {
        //당일
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_TODAY });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (diff == -6) {
        //1주일 전
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_1W });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.setChangeDate(this.endDateString, this.$config.CALENDAR_RANGE_1M) == this.startDateString) {
        //1개월 전
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_1M });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.setChangeDate(this.endDateString, this.$config.CALENDAR_RANGE_3M) == this.startDateString) {
        //3개월 전
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_3M });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.setChangeDate(this.endDateString, this.$config.CALENDAR_RANGE_6M) == this.startDateString) {
        //6개월 전
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_6M });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.setChangeDate(this.endDateString, this.$config.CALENDAR_RANGE_12M) == this.startDateString) {
        //12개월 전, 1년 전
        let index =
          _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_12M }) > -1
            ? _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_12M })
            : _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_1YEAR });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.setChangeDate(this.endDateString, this.$config.CALENDAR_RANGE_2YEAR) == this.startDateString) {
        //2년 전
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_2YEAR });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.setChangeDate(this.endDateString, this.$config.CALENDAR_RANGE_3YEAR) == this.startDateString) {
        //3년 전
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_3YEAR });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.setChangeDate(this.endDateString, this.$config.CALENDAR_RANGE_4YEAR) == this.startDateString) {
        //4년 전
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_4YEAR });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.$getDateToString() < this.endDateString && this.setChangeDate(this.startDateString, this.$config.CALENDAR_RANGE_3M_AFTER) == this.endDateString) {
        //3개월 후
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_3M_AFTER });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.$getDateToString() < this.endDateString && this.setChangeDate(this.startDateString, this.$config.CALENDAR_RANGE_6M_AFTER) == this.endDateString) {
        //6개월 후
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_6M_AFTER });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      } else if (this.$getDateToString() < this.endDateString && this.setChangeDate(this.startDateString, this.$config.CALENDAR_RANGE_12M_AFTER) == this.endDateString) {
        //12개월 후
        let index = _.findIndex(this.filterRangeTextList, { value: this.$config.CALENDAR_RANGE_12M_AFTER });
        if (index > -1) this.$refs.rangeBtn[index].classList.add("selected");
      }
    },
    /**
     * 캘린더 팝업 날짜 클릭
     */
    dayClick() {},
    /**
     * 캘린더 팝업 확인
     */
    confirm() {
      //validation
      const minMonthCode = this.filterRangeTextList[0].value;
      if (minMonthCode != this.$config.CALENDAR_RANGE_ETC) {
        const maxMonthCode = this.filterRangeTextList[this.filterRangeTextList.length - 1].value;
        let maxMonth = 0;

        if (`${this.$config.CALENDAR_RANGE_3M}|${this.$config.CALENDAR_RANGE_3M_AFTER}`.includes(maxMonthCode)) {
          maxMonth = 3;
        } else if (`${this.$config.CALENDAR_RANGE_12M}|${this.$config.CALENDAR_RANGE_1YEAR}|${this.$config.CALENDAR_RANGE_12M_AFTER}`.includes(maxMonthCode)) {
          maxMonth = 12;
        } else if (this.$config.CALENDAR_RANGE_4YEAR == maxMonthCode) {
          maxMonth = 48;
        }

        if (!this.$inDurationMonth(this.startDateString, this.endDateString, maxMonth)) {
          switch (maxMonth) {
            case 3:
              this.validationErrorMsg = "MCOM-0000050"; // 한번에 최대 3개월까지 조회가능합니다.
              break;
            case 12:
              this.validationErrorMsg = "MCOM-0000051"; // 한번에 최대 12개월까지 조회가능합니다.
              break;
            case 48:
              this.validationErrorMsg = "MCOM-0000052"; // 한번에 최대 12개월까지 조회가능합니다.
              break;
          }
          this.validationPopup = true;
          return;
        }
      }

      this.setDate({ start: this.startDateString, end: this.endDateString });
      this.isCalendarOpen = false;
    },
    /**
     * 캘린더 팝업 취소
     */
    cancel() {
      this.setDate({ start: this.fromDate, end: this.toDate });
      this.isCalendarOpen = false;
    },
  },
};
</script>
